import { useMemo } from 'react';
import {
  useMutation,
  useQuery,
  useQueryClient,
  type UseMutationResult,
  type UseQueryResult,
} from '@tanstack/react-query';
import type { ListEnvelope } from '../../../shared/api/genexus-query';
import type { CrudApi, CrudHooks } from '../../../shared/api/crud-hooks';
import type { DataTableQuery } from '../../../shared/components/DataTable';
import { createCompanyDocumentosFiscaisApi } from './api';
import type {
  CompanyDocumentoFiscalDto,
  CompanyDocumentoFiscalInput,
  CompanyDocumentoFiscalListItem,
} from './types';

/**
 * Hooks CRUD para documentos fiscais de uma Empresa. TId=string porque a PK
 * é composta `(modelo, serie)` serializada como `"<modelo>/<serie>"`.
 */
export function useCompanyDocumentosFiscaisHooks(companyId: number): CrudHooks<
  CompanyDocumentoFiscalListItem,
  CompanyDocumentoFiscalDto,
  CompanyDocumentoFiscalInput,
  string
> & {
  api: CrudApi<
    CompanyDocumentoFiscalListItem,
    CompanyDocumentoFiscalDto,
    CompanyDocumentoFiscalInput,
    string
  >;
} {
  const dfApi = useMemo(() => createCompanyDocumentosFiscaisApi(companyId), [companyId]);
  const queryKey = useMemo(
    () => ['companies', companyId, 'documentos-fiscais'] as const,
    [companyId],
  );
  const qc = useQueryClient();

  const useList = (
    query: DataTableQuery,
  ): UseQueryResult<ListEnvelope<CompanyDocumentoFiscalListItem>, Error> =>
    useQuery({
      queryKey: [...queryKey, 'list', query],
      queryFn: () => dfApi.list(query),
      placeholderData: (previous) => previous,
    });

  const useDetail = (
    id: string | null | undefined,
  ): UseQueryResult<CompanyDocumentoFiscalDto, Error> =>
    useQuery({
      queryKey: [...queryKey, 'detail', id],
      queryFn: () => dfApi.get(id as string),
      enabled: id !== null && id !== undefined,
    });

  const useCreate = (): UseMutationResult<
    CompanyDocumentoFiscalDto,
    Error,
    CompanyDocumentoFiscalInput
  > =>
    useMutation({
      mutationFn: (input) => dfApi.create(input),
      onSuccess: () => qc.invalidateQueries({ queryKey }),
    });

  const useUpdate = (): UseMutationResult<
    CompanyDocumentoFiscalDto,
    Error,
    { id: string; input: CompanyDocumentoFiscalInput }
  > =>
    useMutation({
      mutationFn: ({ id, input }) => dfApi.update(id, input),
      onSuccess: () => qc.invalidateQueries({ queryKey }),
    });

  const useDelete = (): UseMutationResult<void, Error, string> =>
    useMutation({
      mutationFn: (id) => dfApi.remove(id),
      onSuccess: () => qc.invalidateQueries({ queryKey }),
    });

  return { api: dfApi, useList, useDetail, useCreate, useUpdate, useDelete };
}
